/**
 * TypeScript mirror of the UDS catalog's schema surface.
 *
 * The PostgreSQL type mapping (`udsDataTypeForColumn`) is codegen'd from the
 * `@udsPostgresDisplay` directives on `enum DataType` in
 * packages/graphql/src/schema/types.graphql, alongside its Rust twin in
 * doss-control-plane — the two cannot drift.
 *
 * The infrastructure columns below still hand-mirror the Rust sources:
 * - `crates/libs/doss-control-plane/src/naming.rs` (`INFRA_COLUMNS`);
 * - `crates/libs/doss-sql-rewrite/src/translation/catalog.rs` for order and types.
 *
 * Drift is caught by the API parity test that compares the GraphQL `tables`
 * query against the wasm-computed UDS catalog for the same user
 * (`packages/api/src/__tests__/resolvers/uds-catalog-parity.test.ts`).
 */

export { udsDataTypeForColumn } from '../generated/uds-postgres-display.generated.js'

/** An infrastructure (`__doss_*`) column present in every visible UDS table. */
export type UdsSystemColumnSpec = {
  /** User-facing slug, e.g. `__doss_id`. */
  slug: string
  /** PostgreSQL type name as reported by the UDS catalog. */
  dataType: string
  /** The UDS catalog reports every infrastructure column as nullable. */
  isNullable: true
}

const infraColumn = (slug: string, dataType: string): UdsSystemColumnSpec => ({
  slug,
  dataType,
  isNullable: true,
})

/** Infrastructure columns present in every z-table, in UDS catalog order. */
const UDS_SYSTEM_COLUMNS_BASE: readonly [UdsSystemColumnSpec, UdsSystemColumnSpec] = [
  infraColumn('__doss_id', 'text'),
  infraColumn('__doss_parent_row_id', 'text'),
]

const UDS_SYSTEM_COLUMNS_TAIL: readonly UdsSystemColumnSpec[] = [
  infraColumn('__doss_created_at', 'timestamp without time zone'),
  infraColumn('__doss_updated_at', 'timestamp without time zone'),
  infraColumn('__doss_created_by', 'text'),
  infraColumn('__doss_updated_by', 'text'),
]

/**
 * The `__doss_*` infrastructure columns the UDS catalog lists for a table,
 * in catalog order (before application columns). `__doss_parent_row_id`
 * exists only on child tables.
 */
export function udsSystemColumnsForTable(hasParentTable: boolean): UdsSystemColumnSpec[] {
  const [dossId, parentRowId] = UDS_SYSTEM_COLUMNS_BASE
  return hasParentTable
    ? [dossId, parentRowId, ...UDS_SYSTEM_COLUMNS_TAIL]
    : [dossId, ...UDS_SYSTEM_COLUMNS_TAIL]
}
